import { useState, useEffect, useCallback } from "react";
import { ethers } from "ethers";
import { FACTORY_ADDRESS, FACTORY_ABI, VAULT_ABI, ERC20_ABI } from "../constants";

export interface VaultInfo {
  address: string;
  name: string;
  lpTokenAddress: string;
  totalLpManaged: string;
  pricePerShare: string;
  pendingRewards: string;
  perfFeeBps: number;
  withdrawFeeBps: number;
  lastHarvest: Date | null;
  totalFeesCollected: string;
  totalRewardsCompounded: string;
  isStable: boolean;
  // User-specific
  userShares: string;
  userLpValue: string;
  userLpBalance: string;
  lpAllowance: string;
}

export function useVaults(
  userAddress: string | null,
  provider: ethers.BrowserProvider | null
) {
  const [vaults, setVaults]   = useState<VaultInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!provider) return;
    setLoading(true);
    setError(null);

    try {
      const factory = new ethers.Contract(FACTORY_ADDRESS, FACTORY_ABI, provider);
      const vaultAddrs: string[] = await factory.getAllVaults();

      const vaultData = await Promise.all(
        vaultAddrs.map(async (vaultAddr): Promise<VaultInfo> => {
          const vault = new ethers.Contract(vaultAddr, VAULT_ABI, provider);

          const [
            name,
            totalLpManaged,
            pricePerShare,
            pendingRewards,
            perfFeeBps,
            withdrawFeeBps,
            lastHarvestTs,
            lpAddr,
            totalFeesCollected,
            totalRewardsCompounded,
            isStable,
          ] = await Promise.all([
            vault.vaultName(),
            vault.totalLpManaged(),
            vault.pricePerShare(),
            vault.pendingRewards(),
            vault.perfFeeBps(),
            vault.withdrawFeeBps(),
            vault.lastHarvestTimestamp(),
            vault.lpToken(),
            vault.totalFeesCollected(),
            vault.totalRewardsCompounded(),
            vault.isStable(),
          ]);

          let userShares   = 0n;
          let userLpValue  = 0n;
          let userLpBal    = 0n;
          let lpAllowance  = 0n;

          if (userAddress) {
            const lp = new ethers.Contract(lpAddr, ERC20_ABI, provider);
            [userShares, userLpBal, lpAllowance] = await Promise.all([
              vault.balanceOf(userAddress),
              lp.balanceOf(userAddress),
              lp.allowance(userAddress, vaultAddr),
            ]);
            if (userShares > 0n) {
              userLpValue = await vault.getLpForShares(userShares);
            }
          }

          return {
            address:               vaultAddr,
            name,
            lpTokenAddress:        lpAddr,
            totalLpManaged:        ethers.formatEther(totalLpManaged),
            pricePerShare:         ethers.formatEther(pricePerShare),
            pendingRewards:        ethers.formatEther(pendingRewards),
            perfFeeBps:            Number(perfFeeBps),
            withdrawFeeBps:        Number(withdrawFeeBps),
            lastHarvest:           lastHarvestTs > 0n ? new Date(Number(lastHarvestTs) * 1000) : null,
            totalFeesCollected:    ethers.formatEther(totalFeesCollected),
            totalRewardsCompounded: ethers.formatEther(totalRewardsCompounded),
            isStable:              Boolean(isStable),
            userShares:            ethers.formatEther(userShares),
            userLpValue:           ethers.formatEther(userLpValue),
            userLpBalance:         ethers.formatEther(userLpBal),
            lpAllowance:           ethers.formatEther(lpAllowance),
          };
        })
      );

      setVaults(vaultData);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load vaults");
    } finally {
      setLoading(false);
    }
  }, [provider, userAddress]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15_000);
    return () => clearInterval(id);
  }, [refresh]);

  return { vaults, loading, error, refresh };
}
